import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { KeyRound, Smartphone } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field } from "../components/Field";
import { Button } from "../components/ui/button";

const useSubmit = (fn) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

const LoginForm = ({ done }) => {
  const [f, setF] = useState({ correo: "", password: "" });
  const [busy, submit] = useSubmit(async () => {
    await api.post("/auth/login", f);
    await done();
  });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Ingresar</h1>
      <Field id="login-correo" label="Correo" type="email" required value={f.correo} onChange={(e) => setF({ ...f, correo: e.target.value })} />
      <Field id="login-password" label="Contraseña" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="login-submit-button">{busy ? "Verificando…" : "Ingresar"}</Button>
      <p className="text-sm text-slate-500 text-center">¿Tu empresa aún no está registrada? <Link to="/registro" className="text-blue-700 underline">Regístrala</Link></p>
    </form>
  );
};

const ChangePassword = ({ done }) => {
  const [f, setF] = useState({ actual: "", nueva: "", nueva2: "" });
  const [busy, submit] = useSubmit(async () => {
    if (f.nueva !== f.nueva2) throw { response: { data: { detail: "Las contraseñas nuevas no coinciden" } } };
    await api.post("/auth/change-password", { actual: f.actual, nueva: f.nueva });
    toast.success("Contraseña actualizada");
    await done();
  });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <KeyRound className="text-blue-700" />
      <h1 className="font-heading text-2xl font-bold tracking-tight">Cambia tu contraseña</h1>
      <p className="text-sm text-slate-600">Es tu primer ingreso. Define una contraseña nueva de al menos 12 caracteres.</p>
      <Field id="cp-actual" label="Contraseña actual" type="password" required value={f.actual} onChange={(e) => setF({ ...f, actual: e.target.value })} />
      <Field id="cp-nueva" label="Nueva contraseña" type="password" required minLength={12} value={f.nueva} onChange={(e) => setF({ ...f, nueva: e.target.value })} />
      <Field id="cp-nueva2" label="Repetir nueva contraseña" type="password" required value={f.nueva2} onChange={(e) => setF({ ...f, nueva2: e.target.value })} />
      <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="change-password-submit">Guardar contraseña</Button>
    </form>
  );
};

const CodeForm = ({ endpoint, done, testId }) => {
  const [codigo, setCodigo] = useState("");
  const [busy, submit] = useSubmit(async () => {
    await api.post(endpoint, { codigo });
    await done();
  });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={testId}>
      <Field id="totp-code" label="Código de 6 dígitos" inputMode="numeric" maxLength={6} required autoComplete="one-time-code"
        className="[&_input]:font-mono [&_input]:tracking-[0.5em] [&_input]:text-lg" value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))} />
      <Button type="submit" disabled={busy || codigo.length !== 6} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="totp-submit-button">Confirmar</Button>
    </form>
  );
};

const TotpSetup = ({ done }) => {
  const [qr, setQr] = useState(null);
  const [busy, gen] = useSubmit(async () => {
    const { data } = await api.post("/auth/totp/setup");
    setQr(data);
  });
  return (
    <div className="space-y-4" data-testid="totp-setup">
      <Smartphone className="text-blue-700" />
      <h1 className="font-heading text-2xl font-bold tracking-tight">Configura tu segundo factor</h1>
      <p className="text-sm text-slate-600">Tu rol exige un código TOTP (Google Authenticator, Microsoft Authenticator, 1Password, etc.). No podrás entrar al panel sin configurarlo.</p>
      {!qr ? (
        <Button onClick={gen} disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="totp-generate-button">Generar código QR</Button>
      ) : (
        <>
          <img src={qr.qr} alt="Código QR TOTP" className="mx-auto w-48 h-48 border border-slate-200 rounded-lg" data-testid="totp-qr" />
          <p className="text-xs text-slate-500 text-center">o ingresa esta clave manualmente:</p>
          <code className="block text-center font-mono text-xs bg-slate-100 text-cyan-700 px-2 py-2 rounded select-all break-all" data-testid="totp-secret">{qr.secreto}</code>
          <CodeForm endpoint="/auth/totp/activate" done={done} testId="totp-activate-form" />
        </>
      )}
    </div>
  );
};

const TotpVerify = ({ done }) => (
  <div className="space-y-4">
    <Smartphone className="text-blue-700" />
    <h1 className="font-heading text-2xl font-bold tracking-tight">Segundo factor</h1>
    <p className="text-sm text-slate-600">Ingresa el código que muestra tu aplicación de autenticación.</p>
    <CodeForm endpoint="/auth/totp/verify" done={done} testId="totp-verify-form" />
  </div>
);

const STEPS = { cambio_password: ChangePassword, totp_setup: TotpSetup, totp_verify: TotpVerify };

export default function Ingresar() {
  const { me, refresh, logout } = useAuth();
  if (me?.etapa === "completa") {
    const volver = sessionStorage.getItem("volver_a");
    sessionStorage.removeItem("volver_a");
    const panel = volver && volver.startsWith("/panel/") ? volver : "/panel";
    return <Navigate to={me.usuario.rol === "superadmin" ? "/admin" : panel} replace />;
  }
  const Step = me ? STEPS[me.etapa] : LoginForm;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 space-y-6 text-slate-900">
        <Link to="/" className="flex items-center"><BrandLogo className="h-8" /></Link>
        <Step done={refresh} />
        {me && (
          <button onClick={logout} className="text-xs text-slate-500 underline w-full text-center" data-testid="login-cancel-button">
            Cancelar y salir ({me.usuario.correo})
          </button>
        )}
      </div>
    </div>
  );
}
